import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Box } from '@storyofams/react-ui';
import { withStory, WithStoryProps } from '@storyofams/storyblok-toolkit';
import { NextPage, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import SbEditable from 'storyblok-react';
import * as Yup from 'yup';

import {
  Button,
  Input,
  Checkbox,
  Divider,
  Heading,
  Seo,
  Text,
} from '~components';
import { useShopify } from '~context';
import {
  enhancedStaticProps,
  getTranslationForLocale,
  passwordSchema,
  withLayout,
} from '~lib';
import { TermsAndConditions } from '~components/forms/TermsAndConditions';
import { Layout } from '~components/login';
import { CustomerCreateInput } from '~lib/shopify/sdk';
import { RegisterpageItem } from '~lib/storyblok/sdk';

const messages = defineMessages({
  firstName: 'First name *',
  firstNamePlaceholder: 'Your first name',
  lastName: 'Last name *',
  lastNamePlaceholder: 'Your last name',
  email: 'Email *',
  emailPlaceholder: 'Your email',
  password: 'Password *',
  passwordPlaceholder: 'Your password',
  passwordRequirements:
    'Your password should be at least 8 characters, contain both uppercase and lowercase letters, and include at least 1 number.',
  acceptsMarketing: 'Keep me updated with updates from Bols',
  createAccount: 'Create my account',
  title: 'Create an account',
  seoDescription: 'Register page',
  welcomeBack: 'Welcome back',
  signIn: 'Sign in',
});

const schema = Yup.object().shape({
  firstName: Yup.string().required('First name is required').trim().min(2),
  lastName: Yup.string().required('Last name is required').trim().min(2),
  email: Yup.string().email().required('Email is required'),
  password: passwordSchema,
  acceptsMarketing: Yup.boolean(),
  acceptsTerms: Yup.boolean()
    .required()
    .oneOf([true], 'Please accept the terms to continue'),
});

const RegisterPage: NextPage<WithStoryProps> = ({ story }) => {
  const intl = useIntl();
  const [disabled, setDisabled] = useState(false);
  const { signUp, customer } = useShopify();
  const router = useRouter();

  const { title, description, seo, termsAndConditions } =
    (story?.content as RegisterpageItem['content']) || {};

  useEffect(() => {
    if (customer?.id) {
      router.push(
        process.env.NEXT_PUBLIC_STORE_COUNTRY === 'b2b' ? '/shop' : '/',
      );
    }
  }, [customer]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CustomerCreateInput & { acceptsTerms: boolean }>({
    resolver: yupResolver(schema),
  });

  const onSubmit = (input) => {
    signUp({ ...input, acceptsTerms: undefined }).then((result) => {
      if (result && (result as any)?.disableForm) {
        setDisabled(true);
      }
    });
  };

  return (
    <>
      <Seo
        {...(seo || {
          title: title || intl.formatMessage(messages.title),
          description: intl.formatMessage(messages.seoDescription),
        })}
      />

      <Layout content={{}}>
        <Heading as="h2" variant="h3" fontSize={[5, 4]} mb={[1, 2]}>
          <FormattedMessage {...messages.welcomeBack} />
        </Heading>

        <Button to="/login" variant="outline">
          <FormattedMessage {...messages.signIn} />
        </Button>
        <Divider my={5} />

        <Box mb={[4, 5]}>
          <SbEditable content={story?.content}>
            <Heading as="h1" variant="h2" mb={description ? 1 : 0}>
              {title || <FormattedMessage {...messages.title} />}
            </Heading>

            {description && (
              <Text mb={[4, 5]} color="grey500" px={{ all: 5, md: 0 }}>
                {description}
              </Text>
            )}
          </SbEditable>
        </Box>

        <Box as="form" onSubmit={handleSubmit(onSubmit)}>
          <Stack flexDirection="column" space={4}>
            <Input
              label={intl.formatMessage(messages.firstName)}
              placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
              error={errors?.firstName?.message}
              disabled={disabled}
              {...register('firstName')}
            />

            <Input
              label={intl.formatMessage(messages.lastName)}
              placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
              error={errors?.lastName?.message}
              disabled={disabled}
              {...register('lastName')}
            />

            <Input
              type="email"
              label={intl.formatMessage(messages.email)}
              placeholder={intl.formatMessage(messages.emailPlaceholder)}
              error={errors?.email?.message}
              disabled={disabled}
              {...register('email')}
            />

            <Input
              type="password"
              label={intl.formatMessage(messages.password)}
              placeholder={intl.formatMessage(messages.passwordPlaceholder)}
              statusMessage={intl.formatMessage(messages.passwordRequirements)}
              error={errors?.password?.message}
              disabled={disabled}
              {...register('password')}
            />

            <Checkbox
              label={intl.formatMessage(messages.acceptsMarketing)}
              error={errors?.acceptsMarketing?.message}
              disabled={disabled}
              {...register('acceptsMarketing')}
            />

            <SbEditable content={termsAndConditions?.content || {}}>
              <TermsAndConditions
                content={termsAndConditions?.content?.termsAndConditions}
                error={errors?.acceptsTerms?.message}
                disabled={disabled}
                {...register('acceptsTerms')}
              />
            </SbEditable>

            <Button
              isLoading={isSubmitting}
              disabled={disabled}
              variant="primary"
              width="100%"
              fontSize={2}
              fontWeight="bold"
              color="white"
              type="submit"
              onSubmit={handleSubmit(onSubmit)}
            >
              {intl.formatMessage(messages.createAccount)}
            </Button>
          </Stack>
        </Box>
      </Layout>
    </>
  );
};

export default withLayout(withStory(RegisterPage));

export const getStaticProps: GetStaticProps<WithStoryProps> =
  enhancedStaticProps(async ({ defaultLocale, locale, sdk }) => {
    let story = null;
    const { lang, needsTranslation } = getTranslationForLocale(
      locale,
      defaultLocale,
    );

    try {
      story = (
        await sdk.registerPage({
          slug: `${needsTranslation ? `${lang}/` : ''}auth/register`,
          lang: needsTranslation ? lang : 'default',
        })
      ).RegisterpageItem;
    } catch (e) {}

    return {
      props: {
        story,
      },
      revalidate: 60,
    };
  });
